import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import ChallengeSignup from "@/components/site/ChallengeSignup";
import { PRIYA } from "@/lib/images";

const HERO_IMG = PRIYA.heroPortrait;
const STORY_IMG = PRIYA.smallSteps;

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="container-refined pt-14 md:pt-20 pb-16 md:pb-24 grid gap-12 md:grid-cols-[1.05fr_1fr] items-center">
          <div className="animate-fade-up">
            <span className="eyebrow">1:1 Coaching for Men &middot; Corporate Wellness</span>
            <h1 className="font-serif mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={{ color: "#1B4332" }}>
              You provide for everyone.{" "}
              <em className="italic" style={{ color: "#2D5A46" }}>Who&rsquo;s holding you up?</em>
            </h1>
            <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
              Life coaching for men who look fine on the outside &mdash; and are quietly running on empty.
            </p>
            <p className="font-script text-4xl md:text-5xl mt-4">Where You&rsquo;re Heard.</p>
            <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "#2A2621" }}>
              The Life Refinery helps working men who carry stress silently &mdash; the promotion that
              felt hollow, the exhaustion you hide at home, the sense that you&rsquo;ve built a life you
              can&rsquo;t feel. Through 1:1 coaching, we move you from stuck and switched-off to clear,
              confident, and purposeful.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-cta-book" className="btn-gold">
                Book a discovery call <ArrowRight size={16} />
              </Link>
              <a href="#challenge" data-testid="hero-cta-challenge" className="btn-outline-forest">
                Try the free 7-day challenge
              </a>
            </div>
          </div>

          <div className="relative">
            <div
              data-testid="hero-image-frame"
              className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border"
              style={{
                borderColor: "rgba(201,168,76,0.6)",
                boxShadow: "0 40px 80px -40px rgba(27,67,50,0.5)",
              }}
            >
              <img
                src={HERO_IMG}
                alt="Priya Kanagaraj, founder of The Life Refinery"
                className="h-full w-full object-cover animate-ken-burns"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(27,67,50,0.35) 100%)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* STORY TEASER */}
      <section className="paper-grain" data-testid="home-story-teaser" style={{ background: "#F3ECD8" }}>
        <div className="container-refined py-20 md:py-28 grid gap-12 md:grid-cols-[1fr_1.1fr] items-center">
          <div className="relative aspect-[5/6] overflow-hidden rounded-[2rem] order-2 md:order-1"
               style={{ boxShadow: "0 40px 80px -40px rgba(27,67,50,0.4)" }}>
            <img src={STORY_IMG} alt="Priya in her study, beside a &lsquo;Small Steps. Big Changes.&rsquo; print" className="h-full w-full object-cover" />
          </div>
          <div className="order-1 md:order-2">
            <span className="eyebrow">Meet Priya</span>
            <h2 className="font-serif mt-4 text-3xl sm:text-4xl md:text-5xl leading-tight">
              I&rsquo;ve sat in the corner office &mdash; and cried in the parking lot.
            </h2>
            <p className="mt-5 text-[15px] md:text-base leading-relaxed" style={{ color: "#2A2621" }}>
              I built the career everyone congratulated me for. I also lost myself inside it. The
              Life Refinery is what I&rsquo;ve made from finding my way back &mdash; a slow,
              honest kind of coaching for people who look like they have it together and are so
              tired of holding it that way.
            </p>
            <p className="mt-4 text-[15px] md:text-base leading-relaxed" style={{ color: "#2A2621" }}>
              No performance. No prescriptions. Just real conversation, refined into the smallest
              next move that actually feels like <em>yours</em>.
            </p>
            <Link to="/about" data-testid="story-cta-more" className="mt-8 inline-flex items-center gap-2 font-serif text-lg italic" style={{ color: "#1B4332" }}>
              Read her full story <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* IS THIS YOU? */}
      <section className="container-refined py-20 md:py-28" data-testid="home-is-this-you">
        <div className="max-w-2xl">
          <span className="eyebrow">Is this you?</span>
          <h2 className="font-serif mt-3 text-3xl sm:text-4xl md:text-5xl leading-tight">
            You&rsquo;re not broken. You&rsquo;re carrying too much on your own.
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {[
            {
              n: "01",
              body:
                "You solve everyone&rsquo;s problems but never mention your own.",
            },
            {
              n: "02",
              body:
                "You got the title, the salary, the respect &mdash; and feel nothing.",
            },
            {
              n: "03",
              body:
                "You&rsquo;re patient at work and short-tempered at home, and you hate it.",
            },
            {
              n: "04",
              body:
                "You can&rsquo;t remember the last time someone asked how you&rsquo;re really doing.",
            },
          ].map((c) => (
            <div
              key={c.n}
              data-testid={`isthisyou-card-${c.n}`}
              className="rounded-2xl border p-8 transition-all hover:-translate-y-1"
              style={{ background: "#FAF6EC", borderColor: "rgba(27,67,50,0.12)" }}
            >
              <span className="font-serif text-2xl" style={{ color: "#C9A84C" }}>{c.n}</span>
              <p className="mt-3 font-serif text-xl md:text-2xl leading-snug" style={{ color: "#1B4332" }}
                 dangerouslySetInnerHTML={{ __html: c.body }} />
            </div>
          ))}
        </div>
        <p className="mt-10 max-w-2xl text-[15px] md:text-base leading-relaxed" style={{ color: "#2A2621" }}>
          If any of that landed &mdash; even quietly &mdash; you&rsquo;re in the right place. And if
          you&rsquo;re a partner or family member reading this on his behalf, you&rsquo;re welcome here too.
        </p>
      </section>

      {/* CHALLENGE SIGNUP */}
      <section id="challenge" className="container-refined pb-24" data-testid="home-challenge-section">
        <ChallengeSignup />
      </section>

      {/* FEEDBACK — Priya's own voice */}
      <section className="pb-24" data-testid="home-feedback">
        <div className="container-refined">
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">A note from Priya</span>
            <h2 className="font-serif mt-3 text-3xl sm:text-4xl md:text-5xl leading-tight">
              What happens in the room
            </h2>
          </div>

          <div
            className="grid gap-8 md:gap-10 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] items-stretch"
            data-testid="home-feedback-grid"
          >
            {/* Left: photo */}
            <div
              className="rounded-[1.5rem] overflow-hidden border self-stretch"
              style={{
                borderColor: "#C9A84C",
                borderWidth: "1px",
                boxShadow: "0 30px 60px -30px rgba(27,67,50,0.35)",
              }}
              data-testid="home-feedback-photo-frame"
            >
              <img
                src="/images/priya-laptop.png"
                alt="Priya Kanagaraj, at her desk"
                className="w-full h-full object-cover"
                style={{ minHeight: 380 }}
                data-testid="home-feedback-photo"
              />
            </div>

            {/* Right: quote panel */}
            <figure
              className="rounded-[1.5rem] px-8 md:px-12 py-10 md:py-12 flex flex-col justify-center"
              style={{ background: "#1B4332", color: "#FAF6EC" }}
              data-testid="home-feedback-quote-panel"
            >
              <Quote size={44} style={{ color: "#C9A84C" }} data-testid="home-feedback-icon" />
              <blockquote
                className="font-serif italic mt-6 text-2xl md:text-3xl leading-snug"
                style={{ color: "#FAF6EC" }}
              >
                &ldquo;Every man who sits across from me starts the same way: &lsquo;I don&rsquo;t
                usually talk about this.&rsquo; By the end, they wish they&rsquo;d started years
                earlier.&rdquo;
              </blockquote>
              <span
                className="block h-px w-14 mt-8"
                style={{ background: "#C9A84C" }}
                data-testid="home-feedback-divider"
                aria-hidden="true"
              />
              <figcaption
                className="mt-4 text-sm tracking-wide"
                style={{ color: "#FAF6EC", fontFamily: "'Jost', sans-serif" }}
                data-testid="home-feedback-attribution"
              >
                Priya Kanagaraj &middot; Founder, The Life Refinery
              </figcaption>
            </figure>
          </div>
        </div>
      </section>
    </div>
  );
}
